import { useState } from "react";
import { X } from "lucide-react";

// Скидка при цене ниже каталожной — YClients сам скидку не считает и не показывает по одной лишь
// заниженной cost_per_unit (см. buildGoodsTransactions на бэке — % там считается и отправляется
// явно), это чисто визуальный превью для той же арифметики на клиенте.
export function discountPercentPreview(catalogPrice, salePrice) {
  if (!catalogPrice || salePrice == null || salePrice === "" || Number(salePrice) >= catalogPrice) return 0;
  return Math.round((1 - Number(salePrice) / catalogPrice) * 100);
}

export function formatPrice(price) {
  if (price === null || price === undefined || price === "") return "";
  return new Intl.NumberFormat("ru-RU").format(price) + " ₽";
}

// details.price — ВСЕГДА цена за единицу, сумма — чисто производное поле для отображения и
// редактирования. Количество реализовано как N отдельных покупок с ОДНОЙ и той же ценой за
// единицу каждая (см. addPurchase/addPickedPurchases) — поэтому сумма, не делящаяся на
// количество нацело, при редактировании поля "Сумма" неизбежно немного округляется обратно в
// цену за единицу (Math.round(sum / quantity)), это ограничение архитектуры, не баг.
export default function ProductQuantityPriceRow({ name, categoryIcon, catalogPrice, details, onChange, onRemove }) {
  const quantity = details.quantity ?? 1;
  const price = details.price;
  const sum = price == null ? null : price * quantity;
  const discount = discountPercentPreview(catalogPrice, price);

  // Пересчёт "сумма -> цена за единицу" коммитится только по blur/Enter, не на каждое нажатие —
  // иначе контролируемый инпут перезаписывает себя ПОСЛЕ округления на каждой цифре (например,
  // ввод "1000" при quantity=3 после первой же цифры "1" отрисовывает round(1/3)*3 = 0 обратно в
  // поле, и дальнейшие нажатия печатают уже поверх "0", а не "1") — свободный ввод в черновике,
  // реальный recompute только при потере фокуса.
  const [sumDraft, setSumDraft] = useState(null);
  const commitSum = () => {
    if (sumDraft === null) return;
    const newSum = sumDraft === "" ? null : Number(sumDraft);
    onChange({ price: newSum == null ? null : Math.round(newSum / quantity) });
    setSumDraft(null);
  };

  const decrement = () => {
    const next = quantity - 1;
    if (next <= 0) {
      if (onRemove) onRemove();
      return;
    }
    onChange({ quantity: next });
  };

  return (
    <div className="flex items-center gap-1.5 pl-2 pr-1 py-1 bg-blue-50 text-blue-700 rounded-lg text-[11px] font-medium">
      <span className="flex-1 min-w-0 truncate">{categoryIcon} {name}</span>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button type="button" onClick={decrement} className="h-5 w-5 flex items-center justify-center rounded bg-white border border-blue-200 hover:bg-blue-100" title="Меньше">
          −
        </button>
        <span className="w-4 text-center font-semibold">{quantity}</span>
        <button type="button" onClick={() => onChange({ quantity: quantity + 1 })} className="h-5 w-5 flex items-center justify-center rounded bg-white border border-blue-200 hover:bg-blue-100" title="Больше">
          +
        </button>
      </div>
      <input
        type="number"
        className="w-16 h-6 px-1.5 rounded border border-blue-200 text-right text-[11px] bg-white flex-shrink-0"
        value={price ?? ""}
        onChange={e => onChange({ price: e.target.value === "" ? null : Number(e.target.value) })}
        title="Цена за единицу"
      />
      <input
        type="number"
        className="w-20 h-6 px-1.5 rounded border border-blue-200 text-right text-[11px] bg-white flex-shrink-0"
        value={sumDraft !== null ? sumDraft : (sum ?? "")}
        onChange={e => setSumDraft(e.target.value)}
        onBlur={commitSum}
        onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
        title="Сумма за всё количество"
      />
      {discount > 0 && (
        <span className="text-red-500 font-semibold flex-shrink-0" title="Скидка от каталожной цены">−{discount}%</span>
      )}
      {onRemove && (
        <button onClick={onRemove} className="text-blue-400 hover:text-red-500 transition-colors flex-shrink-0" title="Убрать товар">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
